import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Ban, RotateCcw, Search, ShieldCheck, Trash2, Users } from "@fleet/icons";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Label, Skeleton, TextField } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import {
  ADMIN_ROLES,
  adminQueryKeys,
  type AdminUserDTO,
  assignableRolesFor,
  canAdministerUser,
  type components,
  filterAdminUsers,
} from "@cartero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type UserListResponse = components["schemas"]["AdminUserListResponse"];

/** Action the URL is asking us to perform on a user (`?user=<id>&action=ban`). */
type UserAction = "ban" | "remove";

export function UserManager() {
  const { t } = useI18n();
  const { can, role: actorRole } = useAdminCapabilities();
  const search = useSearch({ from: "/_app/admin/users" });
  const navigate = useNavigate();

  const [banReason, setBanReason] = createSignal("");

  const usersQuery = createApiQuery<UserListResponse, ErrorModel>(
    () => adminQueryKeys.users(),
    async () => api.GET("/api/admin/users", {}),
    () => ({ staleTime: 15_000 }),
  );

  const users = createMemo<AdminUserDTO[]>(() => usersQuery.data?.data ?? []);

  const setRoleMutation = createApiMutation(async (vars: { user_id: string; role: string }) =>
    api.POST("/api/admin/set-role", { body: vars }),
  );
  const banMutation = createApiMutation(async (vars: { user_id: string; reason?: string }) =>
    api.POST("/api/admin/ban-user", { body: vars }),
  );
  const unbanMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/unban-user", { body: vars }),
  );
  const revokeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/revoke-sessions", { body: vars }),
  );
  const removeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/remove-user", { body: vars }),
  );

  const result = createMemo(() =>
    filterAdminUsers(users(), {
      q: search().q,
      role: search().role,
      page: search().page,
    }),
  );

  /** Patch the URL search params (keeps list state addressable, #15b). */
  const patchSearch = (patch: Record<string, unknown>) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>), ...patch };
        for (const key of Object.keys(next)) {
          if (next[key] === undefined || next[key] === "") {
            delete next[key];
          }
        }
        return next;
      },
      replace: true,
    });
  };

  const dialogUser = createMemo<AdminUserDTO | undefined>(() => {
    const id = search().user;
    return id ? users().find((u) => u.id === id) : undefined;
  });
  const dialogAction = (): UserAction | undefined => search().action;

  const closeDialog = () => {
    setBanReason("");
    patchSearch({ user: undefined, action: undefined });
  };

  const openDialog = (userId: string, action: UserAction) => {
    patchSearch({ user: userId, action });
  };

  const afterMutation = (message: string) => {
    void usersQuery.refetch();
    showToast(message, "success");
  };

  /** Table filter: lists ALL roles — it is a search, not a grant. */
  const filterRoleOptions = createMemo(() => ADMIN_ROLES.map((r) => ({ value: r, label: r })));

  /**
   * Per-user role selector: only what THIS admin can grant. It previously
   * offered the entire list (`ASSIGNABLE_ROLES = ADMIN_ROLES`), so an
   * `admin` could choose `super_admin` — and the backend accepted it.
   */
  const assignableRoleOptions = createMemo(() =>
    assignableRolesFor(actorRole()).map((r) => ({ value: r, label: r })),
  );

  const handleBan = () => {
    const user = dialogUser();
    if (!user) {
      closeDialog();
      return;
    }
    banMutation.mutate(
      { user_id: user.id, reason: banReason().trim() || undefined },
      {
        onSuccess: () => {
          afterMutation(t(msg`User banned`));
          closeDialog();
        },
      },
    );
  };

  const handleRemove = () => {
    const user = dialogUser();
    if (!user) {
      closeDialog();
      return;
    }
    removeMutation.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          afterMutation(t(msg`User removed`));
          closeDialog();
        },
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <Users class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Users`)}</h2>
          <p class="text-sm text-muted-foreground">
            {(() => {
              const { total } = result();
              return t(msg`${total} users match`);
            })()}
          </p>
        </div>
      </div>

      <Card class="bg-primary/5 border-primary/10">
        <CardContent class="p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div class="relative flex-1">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <TextField
              placeholder={t(msg`Search by email or name…`)}
              class="pl-9"
              value={search().q ?? ""}
              onInput={(e) => {
                patchSearch({ q: e.currentTarget.value, page: undefined });
              }}
            />
          </div>
          <div class="w-full sm:w-48">
            <Select<string>
              aria-label={t(msg`Filter by role`)}
              placeholder={t(msg`All roles`)}
              value={search().role ?? ""}
              options={[{ value: "", label: t(msg`All roles`) }, ...filterRoleOptions()]}
              onChange={(v) => {
                patchSearch({ role: v || undefined, page: undefined });
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* P23 / CC-60 · I3: "no response" != "nothing here". The block is
          WRAPPED (not chained): on error `isLoading` es `false`, so a
          `when={!isLoading && !isError}` with skeleton `fallback` would leave the error
          box and the skeleton at the same time, forever. */}
      <Show when={usersQuery.isError}>
        <QueryErrorState
          testId="users-error"
          compact
          title={t(msg`Couldn't load the user list`)}
          onRetry={() => void usersQuery.refetch()}
        />
      </Show>
      <Show when={!usersQuery.isError}>
        <Show
          when={!usersQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 6 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For
              each={result().rows}
              fallback={
                <Card>
                  <CardContent class="p-8 text-center text-sm text-muted-foreground">
                    {t(msg`No users found.`)}
                  </CardContent>
                </Card>
              }
            >
              {(user) => (
                <Card>
                  <CardContent class="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div class="min-w-0 flex items-center gap-3">
                      <Avatar
                        userId={user.id}
                        name={user.name}
                        email={user.email}
                        image={user.image}
                        size="md"
                      />
                      <div class="min-w-0">
                        <div class="font-bold truncate flex items-center gap-2">
                          {user.name || t(msg`(no name)`)}
                          <Show when={user.banned}>
                            <Badge variant="destructive">{t(msg`Banned`)}</Badge>
                          </Show>
                        </div>
                        <div class="text-xs text-muted-foreground truncate">{user.email}</div>
                      </div>
                    </div>

                    <div class="flex flex-wrap items-center gap-2">
                      <Show
                        when={can("users.setRole") && canAdministerUser(actorRole(), user.role)}
                        fallback={
                          <Badge variant="secondary" class="capitalize">
                            {user.role}
                          </Badge>
                        }
                      >
                        <div class="w-36">
                          <Select<string>
                            aria-label={t(msg`Role`)}
                            value={user.role}
                            options={assignableRoleOptions()}
                            onChange={(role) => {
                              setRoleMutation.mutate(
                                { user_id: user.id, role },
                                { onSuccess: () => afterMutation(t(msg`Role updated`)) },
                              );
                            }}
                          />
                        </div>
                      </Show>

                      <Show when={can("users.ban") && canAdministerUser(actorRole(), user.role)}>
                        <Show
                          when={user.banned}
                          fallback={
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDialog(user.id, "ban")}
                            >
                              <Ban class="mr-1.5 h-4 w-4" />
                              {t(msg`Ban`)}
                            </Button>
                          }
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              unbanMutation.mutate(
                                { userId: user.id },
                                { onSuccess: () => afterMutation(t(msg`User unbanned`)) },
                              );
                            }}
                          >
                            <ShieldCheck class="mr-1.5 h-4 w-4" />
                            {t(msg`Unban`)}
                          </Button>
                        </Show>
                      </Show>

                      <Show
                        when={
                          can("users.revokeSessions") && canAdministerUser(actorRole(), user.role)
                        }
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            revokeMutation.mutate(
                              { userId: user.id },
                              { onSuccess: () => afterMutation(t(msg`Sessions revoked`)) },
                            );
                          }}
                        >
                          <RotateCcw class="mr-1.5 h-4 w-4" />
                          {t(msg`Revoke sessions`)}
                        </Button>
                      </Show>

                      <Show when={can("users.remove") && canAdministerUser(actorRole(), user.role)}>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-destructive hover:text-destructive"
                          onClick={() => openDialog(user.id, "remove")}
                        >
                          <Trash2 class="mr-1.5 h-4 w-4" />
                          {t(msg`Remove`)}
                        </Button>
                      </Show>
                    </div>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>

          <Show when={result().totalPages > 1}>
            <div class="flex items-center justify-center gap-4 pt-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={result().page <= 1}
                onClick={() => patchSearch({ page: result().page - 1 })}
              >
                {t(msg`Previous`)}
              </Button>
              <span class="text-sm text-muted-foreground">
                {(() => {
                  const { page } = result();
                  const { totalPages } = result();
                  return t(msg`Page ${page} of ${totalPages}`);
                })()}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={result().page >= result().totalPages}
                onClick={() => patchSearch({ page: result().page + 1 })}
              >
                {t(msg`Next`)}
              </Button>
            </div>
          </Show>
        </Show>
      </Show>

      <Dialog
        open={Boolean(dialogUser()) && dialogAction() === "ban"}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        contentClass="max-w-[420px] p-6"
      >
        <DialogTitle class="text-xl font-bold mb-2">{t(msg`Ban user`)}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-4">
          {t(msg`The user will be unable to sign in. You can provide an optional reason.`)}
        </DialogDescription>
        <div class="space-y-2">
          <Label for="ban-reason">{t(msg`Reason (optional)`)}</Label>
          <TextField
            id="ban-reason"
            value={banReason()}
            onInput={(e) => {
              setBanReason(e.currentTarget.value);
            }}
          />
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={closeDialog}>
            {t(msg`Cancel`)}
          </Button>
          <Button variant="destructive" disabled={banMutation.isPending} onClick={handleBan}>
            {t(msg`Ban user`)}
          </Button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={Boolean(dialogUser()) && dialogAction() === "remove"}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        title={t(msg`Remove user (GDPR)`)}
        description={t(
          msg`This permanently deletes the user and all their data. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Permanently remove`)}
        variant="destructive"
        isLoading={removeMutation.isPending}
        onConfirm={handleRemove}
        onCancel={closeDialog}
      />
    </div>
  );
}
